import React, { useState, useEffect } from 'react';
import { Form, Select, Button, Typography, message, Card, Spin, Space, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import { BankOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuthStore, Client } from '../store/authStore';
import { clientAPI } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

interface ClientSelectionFormValues {
  clientId: string;
}

const ClientSelectionPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user, availableClients, setSelectedClient, setAvailableClients, login } = useAuthStore();

  const BrandIcon = () => (
    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
      <img 
        src="/logo.png" 
        alt="Logo" 
        style={{ 
          width: '250px', 
          height: '250px',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto'
        }} 
      />
    </div>
  );

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      
      // For super admin, always clear any selected client and impersonation state when fetching all clients
      if (user?.role === 'super_admin') {
        setSelectedClient(null);
        
        // Also clear any impersonation state if present
        const { clearImpersonation, isImpersonating } = useAuthStore.getState();
        if (isImpersonating) {
          clearImpersonation();
        }
      }
      
      // Use the proper API to fetch clients
      const response = await clientAPI.getClients();
      
      if (response.data && response.data.success) {
        const clients = response.data.clients || [];
        console.log('Fetched clients:', clients);
        setAvailableClients(clients);
        
        if (clients.length === 0) {
          message.info('No clients available');
        }
      } else {
        console.error('Failed to fetch clients:', response.data);
        setAvailableClients([]);
        message.error('Failed to load clients');
      }
      
    } catch (error: any) {
      console.error('Error fetching clients:', error);
      setAvailableClients([]);
      
      const errorMessage = error.response?.data?.message || 'Failed to load clients';
      message.error(errorMessage);
      
    } finally {
      setClientsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchClients();
    setRefreshing(false);
    message.success('Client list refreshed');
  };

  const handleDeleteClient = async (clientId: string) => {
    try {
      // Call backend API to delete client
      // Note: This should only be available to super admins
      const response = await clientAPI.deleteClient(clientId);
      
      if (response.data && response.data.success) {
        message.success('Client deleted successfully');
        await fetchClients(); // Refresh the list
      } else {
        message.error('Failed to delete client');
      }
    } catch (error: any) {
      console.error('Error deleting client:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete client';
      message.error(errorMessage);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [setAvailableClients]);

  const onFinish = async (values: ClientSelectionFormValues) => {
    setLoading(true);
    try {
      const selectedClient = availableClients.find(client => client.id === values.clientId);
      
      if (!selectedClient) {
        message.error('Invalid client selection');
        return;
      }

      // For super admins, we need to impersonate the company admin
      if (user?.role === 'super_admin') {
        try {
          // Call API to get company admin credentials for impersonation
          const impersonateResponse = await clientAPI.impersonateClientAdmin(values.clientId);
          
          if (impersonateResponse.data && impersonateResponse.data.success) {
            // Store original super admin user and set up impersonation
            const { setImpersonation } = useAuthStore.getState();
            setImpersonation({
              originalUser: user, // Store the original super admin user
              impersonatedUser: impersonateResponse.data.user, // The company admin user
              selectedClient: impersonateResponse.data.client
            });
            
            // Update the token
            useAuthStore.setState({ token: impersonateResponse.data.token });
            
            message.success(`Now managing ${selectedClient.name} as their admin`);
          } else {
            throw new Error('Failed to get admin access for client');
          }
        } catch (impersonateError: any) {
          console.error('Impersonation failed:', impersonateError);
          message.error('Failed to access client admin account. Using limited super admin view.');
          // Fall back to regular client selection
          setSelectedClient(selectedClient);
        }
      } else {
        // Regular client selection for company admins
        setSelectedClient(selectedClient);
        message.success(`Selected client: ${selectedClient.name}`);
      }
      
      navigate('/dashboard');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to select client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Card className="auth-card" style={{ maxWidth: '600px' }}>
        <div className="brand-header" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <BrandIcon />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <Title level={2} className="page-title" style={{ margin: 0 }}>
            Select Client
          </Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={refreshing}
            type="text"
            style={{ color: '#47569e' }}
          >
            Refresh
          </Button>
        </div>
        
        <Text style={{ 
          color: '#47569e', 
          display: 'block', 
          textAlign: 'center', 
          marginBottom: '2rem' 
        }}>
          Welcome back, {user?.name || user?.email}! 
          {user?.role === 'super_admin' 
            ? ' Please select a client to manage.' 
            : ' Please select your client to continue.'
          }
        </Text>

        {clientsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: '1rem', color: '#47569e' }}>
              Loading available clients...
            </Text>
          </div>
        ) : (
          <Form
            form={form}
            name="client-selection"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Form.Item
              label={<span className="form-item-label">Available Clients</span>}
              name="clientId"
              rules={[{ required: true, message: 'Please select a client!' }]}
            >
              <Select 
                placeholder="Select a client"
                size="large"
                style={{ height: '56px' }}
              >
                {availableClients.map(client => (
                  <Option key={client.id} value={client.id}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <BankOutlined style={{ marginRight: '12px', color: '#607afb' }} />
                        <div>
                          <div style={{ fontWeight: 500, color: '#0d0f1c' }}>
                            {client.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#47569e' }}>
                            {client.domain}
                          </div>
                        </div>
                      </div>
                      
                      {/* Delete button for super admin only */}
                      {user?.role === 'super_admin' && (
                        <Popconfirm
                          title="Delete Client"
                          description={`Are you sure you want to delete "${client.name}"? This action cannot be undone.`}
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            handleDeleteClient(client.id);
                          }}
                          onCancel={(e) => e?.stopPropagation()}
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={(e) => e.stopPropagation()}
                            style={{ marginLeft: '8px' }}
                          />
                        </Popconfirm>
                      )}
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: '2rem' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                className="login-btn"
                disabled={availableClients.length === 0}
              >
                Continue
              </Button>
            </Form.Item>
          </Form>
        )}

        {!clientsLoading && availableClients.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <Text style={{ color: '#47569e' }}>
              No clients available. Please contact your administrator.
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClientSelectionPage;
